import { useCallback, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { splunk } from '@fs/zion-splunk-logger'

import mutations from '../mutations'
import useGroupData from './useGroupData'
import useCoverages from './useCoverages'
import useGroupDataUpdate from './useGroupDataUpdate'
import removeTypenamesAndPublished from '../helpers/removeTypenamesAndPublished'
import fsOperationsCallHeader from '../../../utils/fsOperationsCallHeader'

export default function useGroupDataForm({ forVolumeSet = false } = {}) {
  const { editMode, group, initialGroup, updating, groupCreatorCheckboxes } = useGroupData()

  const coverages = useMemo(() => group?.coverages || [], [group])
  const metadata = useMemo(() => group?.metadata || {}, [group])
  const id = useMemo(() => group?.id || 0, [group])

  const { coverageCheckboxes } = useCoverages({ forVolumeSet })
  const dispatch = useGroupDataUpdate()

  const [updateGroupData] = useMutation(mutations.updateGroupData())

  const deleteCoveragesMode = useMemo(() => {
    return getIsAnyCheckboxChecked(coverageCheckboxes)
  }, [coverageCheckboxes])

  const deleteCreatorMode = useMemo(() => {
    if (!groupCreatorCheckboxes) return null
    return getIsAnyCheckboxChecked(groupCreatorCheckboxes)
  }, [groupCreatorCheckboxes])

  const setEdited = useCallback(
    (value) => {
      dispatch({ type: 'SET_EDITED', value })
    },
    [dispatch]
  )

  const getCurrentChildGroup = useCallback(() => {
    return initialGroup.childGroups?.find((childGroup) => childGroup.id === id) || initialGroup
  }, [id, initialGroup])

  const saveGroupData = useCallback(
    async (dataToSave = undefined, isRestore = false) => {
      const currentChild = getCurrentChildGroup()
      const coveragesToSave = dataToSave?.coverages
        ? removeExtraDataFromCoverages(dataToSave.coverages, true)
        : getCoveragesToSave(coverages, isRestore ? coverages : currentChild?.coverages)
      const metadataToSave = dataToSave?.metadata
        ? removeTypenamesAndPublished(dataToSave.metadata, true)
        : getMetadataToSave(metadata, isRestore ? metadata : currentChild?.metadata)

      if (metadataToSave.volume || metadataToSave.volume === '') delete metadataToSave.volume

      const result = await updateGroupData({
        variables: {
          updateParams: {
            id,
            imageViewerCoverages: coveragesToSave,
            metadata: metadataToSave,
          },
        },
        context: { headers: { ...fsOperationsCallHeader } },
      })

      if (result?.data?.updateGroupData?.group) {
        const value = result.data.updateGroupData
        dispatch({
          type: 'UPDATE_GROUP_DATA',
          value,
        })
      }
      if (result?.data?.updateGroupData?.errorFields?.length > 0 || result?.errors || !result) {
        splunk.log({
          message: 'Error saving group data',
          errorFields: result?.data?.updateGroupData?.errorFields,
          updateParams: {
            id,
            imageViewerCoverages: coveragesToSave,
            metadata: metadataToSave,
          },
        })
      }
      return result?.data?.updateGroupData
    },
    [coverages, dispatch, getCurrentChildGroup, id, metadata, updateGroupData]
  )

  return {
    deleteCoveragesMode,
    deleteCreatorMode,
    editMode,
    saveGroupData,
    updating,
    setEdited,
  }
}

function getCoveragesToSave(newCoverages, originalCoverages) {
  let coveragesToSave = []
  if (JSON.stringify(newCoverages) !== JSON.stringify(originalCoverages)) {
    coveragesToSave = newCoverages
  }
  coveragesToSave = coveragesToSave?.filter((el) => !!el.place.fullName) // Filter out placeholder places
  return removeExtraDataFromCoverages(coveragesToSave, true)
}

function getMetadataToSave(newMetadata, originalMetadata) {
  const keysToAdd = ['title', 'creator', 'archivalReferenceNumber', 'languages']
  const dataToSave = keysToAdd.reduce((prev, curr) => {
    let newVal
    if (typeof newMetadata[curr] === 'string') {
      newVal = newMetadata[curr].trim()
    } else if (curr === 'creator') {
      // creator was changed to an object to give each value a unique identifier, now we need to change it back to a list
      newVal = Object.values(newMetadata.creator || {})
    } else {
      newVal = newMetadata[curr]
    }

    if (!getDoValuesMatch(newVal, originalMetadata[curr])) {
      return { ...prev, [curr]: newVal }
    }
    return prev
  }, {})

  // the volume field is a string that keeps track of the value in the text input
  // the volumes field is an array of all the volumes and is what gets sent to RMS
  // this splits the volume inputs into an array, removes empty lines, and cleans text
  const newVolume = newMetadata.volume
    ?.split(/\n/i)
    .reduce((prev, volume) => (volume ? [...prev, volume.trim()] : prev), [])
  if (!getDoValuesMatch(newVolume, originalMetadata.volumes)) dataToSave.volumes = newVolume

  return dataToSave
}

function getDoValuesMatch(newVal, oldVal) {
  return Array.isArray(newVal) || Array.isArray(oldVal) ? isShallowArrayEqual(newVal, oldVal) : newVal === oldVal
}

function isShallowArrayEqual(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((val, index) => val === b[index])
}

function getIsAnyCheckboxChecked(data = []) {
  if (Array.isArray(data)) {
    return data.some((item) => getIsAnyCheckboxChecked(item))
  }
  if (typeof data === 'object' && data !== null) {
    return Object.values(data).some((value) => getIsAnyCheckboxChecked(value))
  }
  if (typeof data === 'boolean') {
    // Directly return boolean values
    return data
  }
  return false
}

/**
 * Cleans up the coverages so GraphQL is happy
 */
function removeExtraDataFromCoverages(coverages, removePublished = false) {
  const coveragesWithoutDateOriginalProperty = removeOriginalPropertyFromDates(coverages)
  const coveragesWithoutPlaceDateRange = removePlaceDateRangeFromCoverages(coveragesWithoutDateOriginalProperty)
  return removeTypenamesAndPublished(coveragesWithoutPlaceDateRange, removePublished)
}

/**
 * Remove fromYear/toYear from each place; GraphQL UpdateGroupData expects place with only fullName, id, name
 */
function removePlaceDateRangeFromCoverages(coverages) {
  if (!coverages?.length) return coverages
  return coverages.map((item) => {
    if (item.place && (item.place.fromYear != null || item.place.toYear != null)) {
      const { fromYear, toYear, ...placeRest } = item.place
      return { ...item, place: placeRest }
    }
    return item
  })
}

/**
 * An original property was added to help with BC dates, but it needs to be removed before saving
 */
function removeOriginalPropertyFromDates(coverages) {
  return coverages.map((item) => {
    if (item.recordTypes) {
      item.recordTypes.forEach((recordType) => {
        recordType.date.forEach((dateObj) => {
          if (Object.prototype.hasOwnProperty.call(dateObj, 'original')) {
            delete dateObj.original
          }
          if (Object.prototype.hasOwnProperty.call(dateObj, 'localKey')) {
            delete dateObj.localKey
          }
        })
      })
    }
    return item
  })
}
